/**
 * Weather Station Explorer - Configuration-Driven Architecture
 * 
 * Uses ConfigurableBaseExplorer with clean configuration for Weather Station Explorer
 * following the same pattern as EnhancedDutyLogExplorer.
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import ConfigurableBaseExplorer from '../baseExplorer/ConfigurableBaseExplorer';
import ContextHelp from '../../../easymap/ContextHelp/ContextHelp';
import { cleanStationConfig } from './cleanStationConfig';
import { WeatherStationEntityEntry } from '../../../domain/model/weatherStation';
import { StationStatusData } from '../../../types/weatherData';
import { getLatestReadout } from './latestReadout';
import { getRain24hTotal } from './rainTotal';
import { toChronological } from './chartOrder';
import { alignGustToWind } from './windChart';
import { AVAILABLE_SENSORS } from '../../../types/weatherStation';
import { useFusionAuth } from '@fusionauth/react-sdk';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFire, faWind, faTowerBroadcast, faPlus } from '@fortawesome/free-solid-svg-icons';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, ReferenceLine, Legend } from 'recharts';
import SpreadEventWindrose from './SpreadEventWindrose';
import HourlyWindrose from './HourlyWindrose';
import WeatherStationCreateModal from './WeatherStationCreateModal';
import { getBackendUrl, isDevMode } from '../../../services/configService';
import { usePermissions } from '../../../context/PermissionContext';
import { useTools } from '../../../context/ToolContext';
import StationForm from './StationForm';
import { CFFDRSWizard } from './CFFDRSWizard';
import { CFFDRSAdminModal } from './CFFDRSAdminModal';
import AppModal from '../../common/AppModal';
import { useConfirm } from '../../common/AppConfirm';
import mapboxgl from 'mapbox-gl';
import './WeatherStationExplorer.scss';

// Weather Station specific interfaces
interface WeatherStationExplorerProps {
    className?: string;
    style?: React.CSSProperties;
    onClose?: () => void;
    onSelectionChange?: (selection: WeatherStationEntityEntry[]) => void;
    onDataChange?: () => void;
    height?: number;
    width?: number;
    isWindowed?: boolean;
    windowTitle?: string;
    initialPosition?: { x: number; y: number };
    initialSize?: { width: number; height: number };
    draggable?: boolean;
    resizable?: boolean;
    showCloseButton?: boolean;
    showMinimizeButton?: boolean;
}

// Helper function to format trend data for Recharts with color breakpoints
const formatTrendData = (trendData: any[], dataType: 'temperature' | 'humidity' | 'windSpeed' | 'precipitation') => {
    return trendData.map((point, index) => {
        let color;
        let unit;

        switch (dataType) {
            case 'temperature':
                color = point.value >= 0 ? '#d32f2f' : '#1976d2'; // red ≥ 0°C, blue < 0°C
                unit = '°C';
                break;
            case 'precipitation':
                // Single dark blue: rain is rendered as bars, so it is already
                // distinguishable from the humidity line without relying on hue.
                color = '#0d47a1';
                unit = 'mm';
                break;
            case 'humidity':
                if (point.value < 25) {
                    color = '#888888'; // grey < 25%
                } else if (point.value >= 25 && point.value < 75) {
                    color = '#ff9800'; // orange 25-74%
                } else {
                    color = '#1976d2'; // blue ≥ 75%
                }
                unit = '%';
                break;
            case 'windSpeed':
                if (point.value < 15) {
                    color = '#888888'; // grey < 15 km/h
                } else if (point.value >= 15 && point.value < 30) {
                    color = '#1976d2'; // blue 15-29 km/h
                } else {
                    color = '#ff9800'; // orange ≥ 30 km/h
                }
                unit = ' km/h';
                break;
            default:
                color = '#666666';
                unit = '';
        }

        return {
            index,
            time: new Date(point.time).toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
            }),
            value: point.value,
            color: color,
            unit: unit,
            dataType: dataType,
            fullTime: new Date(point.time).toLocaleString()
        };
    });
};

// Custom Dot component for dynamic colors
const CustomDot = (props: any) => {
    const { cx, cy, payload } = props;
    if (!payload) return null;

    return (
        <circle
            cx={cx}
            cy={cy}
            r={3}
            fill={payload.color}
            stroke={payload.color}
            strokeWidth={1}
        />
    );
};

// Custom Active Dot component for hover state
const CustomActiveDot = (props: any) => {
    const { cx, cy, payload } = props;
    if (!payload) return null;

    return (
        <circle
            cx={cx}
            cy={cy}
            r={5}
            fill={payload.color}
            stroke="#fff"
            strokeWidth={2}
        />
    );
};// Custom Tooltip component for better data display
const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
        const data = payload[0].payload;
        return (
            <div style={{
                backgroundColor: 'var(--em3-color-background-panel)',
                color: 'var(--em3-color-text-primary)',
                border: '1px solid var(--em3-color-border)',
                borderRadius: '4px',
                padding: '8px',
                fontSize: '12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}>
                <p style={{ margin: 0, fontWeight: 'bold' }}>{data.fullTime}</p>
                <p style={{ margin: 0, color: payload[0].color }}>
                    Value: {payload[0].value}{data.unit || ''}
                </p>
            </div>
        );
    }
    return null;
};

// Enhanced Weather Station Detail Modal Component with Windroses
export const WeatherStationDetailModal: React.FC<{
    station: any;
    isOpen: boolean;
    onClose: () => void;
    onUpdate?: () => void;
    onMinimizeExplorer?: () => void;
    initialEditing?: boolean;
}> = ({ station, isOpen, onClose, onUpdate, onMinimizeExplorer, initialEditing = false }) => {
    const [editing, setEditing] = React.useState(initialEditing);

    // Reflect external mode change (e.g. user clicked Edit in the grid
    // while the modal was already mounted for a different station).
    React.useEffect(() => {
        if (isOpen) setEditing(initialEditing);
    }, [isOpen, initialEditing, station?.id]);
    const [stationStatus, setStationStatus] = React.useState<StationStatusData | null>(null);
    const [loadingStatus, setLoadingStatus] = React.useState(false);
    const [statusError, setStatusError] = React.useState<string | null>(null);

    // #886 — the edit form's own Cancel button guards unsaved changes, but the
    // modal chrome's X was a separate path straight to onClose, so a half-finished
    // edit vanished without a prompt. The create modal already tracks the form's
    // dirty state through StationForm's onDirtyChange and routes its close through
    // a guard; the edit modal has to do the same or the two disagree about what
    // the same gesture means.
    const askConfirm = useConfirm();
    const [stationFormDirty, setStationFormDirty] = React.useState(false);

    const tryClose = React.useCallback(async () => {
        if (!stationFormDirty) {
            setEditing(false);
            return;
        }
        const discard = await askConfirm({
            title: 'Unsaved changes',
            message: 'You have unsaved changes. Are you sure you want to exit?',
            confirmLabel: 'Discard changes',
            destructive: true,
        });
        if (discard) {
            setStationFormDirty(false);
            setEditing(false);
        }
    }, [stationFormDirty, askConfirm]);

    const { canDo } = usePermissions();
    const canEditStation = canDo('stations', 'update');
    // #885 — gated on the same operation the backend enforces via
    // requireOperation('stations','delete'), so the control appears for exactly
    // the roles the API will accept: admin, limited_station_mutation and COMMTech.
    const canDeleteStation = canDo('stations', 'delete');
    const { openTool } = useTools();
    const [showCFFDRSModal, setShowCFFDRSModal] = useState(false);
    const [showAdminModal, setShowAdminModal] = useState(false);

    // Fetch station status when modal opens
    React.useEffect(() => {
        if (isOpen && station?.nesdis) {
            fetchStationStatus();
        }
    }, [isOpen, station?.nesdis]);

    const fetchStationStatus = async () => {
        if (!station?.nesdis) return;

        setLoadingStatus(true);
        setStatusError(null);

        try {
            const backendUrl = getBackendUrl();
            const token = getAuthToken();

            const headers: HeadersInit = { 'Content-Type': 'application/json' };
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await fetch(`${backendUrl}/api/weather-data/status/${station.nesdis}`, {
                method: 'GET',
                headers,
                credentials: 'include',
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch station status: ${response.status}`);
            }

            const result = await response.json();
            if (result.success) {
                setStationStatus(result.data);
                console.log('Station Status Object:', result.data);
            } else {
                throw new Error(result.error || 'Failed to fetch station status');
            }
        } catch (error) {
            console.error('Error fetching station status:', error);
            setStatusError(error instanceof Error ? error.message : 'Unknown error');
        } finally {
            setLoadingStatus(false);
        }
    };

    const getAuthToken = (): string | null => {
        const accessToken = localStorage.getItem('access_token');

        if (!accessToken) {
            if (isDevMode()) {
                return 'dev-mode-token';
            } else {
                return null;
            }
        }
        return accessToken;
    };

    // Helper function to get unit for a sensor token
    const getSensorUnit = (token: string): string => {
        const sensor = AVAILABLE_SENSORS.find(s => s.token === token);
        return sensor?.unit || '';
    };

    // Helper function to format value with unit
    const formatWithUnit = (value: number | string | null, token: string): string => {
        if (value === null || value === undefined) return 'N/A';
        const unit = getSensorUnit(token);
        return unit ? `${value}${unit}` : `${value}`;
    };

    if (!isOpen || !station) return null;

    // Coordinate conversion utility functions
    const convertToDM = (decimal: number, type: 'lat' | 'lon'): string => {
        const abs = Math.abs(decimal);
        const degrees = Math.floor(abs);
        const minutes = (abs - degrees) * 60;
        const direction = type === 'lat' ? (decimal >= 0 ? 'N' : 'S') : (decimal >= 0 ? 'E' : 'W');
        return `${degrees}° ${minutes.toFixed(3)}'${direction}`;
    };

    const convertToDMS = (decimal: number, type: 'lat' | 'lon'): string => {
        const abs = Math.abs(decimal);
        const degrees = Math.floor(abs);
        const minutesFloat = (abs - degrees) * 60;
        const minutes = Math.floor(minutesFloat);
        const seconds = (minutesFloat - minutes) * 60;
        const direction = type === 'lat' ? (decimal >= 0 ? 'N' : 'S') : (decimal >= 0 ? 'E' : 'W');
        return `${degrees}° ${minutes}' ${seconds.toFixed(1)}"${direction}`;
    };

    return (
        <>
        {editing ? (
            <AppModal
                isOpen={isOpen}
                onClose={tryClose}
                width={600}
                maxHeight="90vh"
                header="Edit Weather Station"
                persistent
            >
                <StationForm
                    station={station}
                    onDirtyChange={setStationFormDirty}
                    isEditing={true}
                    onSave={async (data) => {
                        try {
                            console.log('[WSE DetailModal] Updating station with data:', data);
                            if (cleanStationConfig.serviceAdapter.updateEntity && station?.id) {
                                await cleanStationConfig.serviceAdapter.updateEntity(String(station.id), data);
                                console.log('[WSE DetailModal] Station updated successfully');
                                setStationFormDirty(false);
                                setEditing(false);
                                onClose();
                                // Trigger grid reload
                                if (onUpdate) {
                                    onUpdate();
                                }
                            } else {
                                throw new Error('updateEntity method not available or station ID missing');
                            }
                        } catch (error) {
                            console.error('[WSE DetailModal] Error updating station:', error);
                            throw error;
                        }
                    }}
                    onCancel={() => { setStationFormDirty(false); setEditing(false); }}
                    onDelete={canDeleteStation ? async (stationId) => {
                        try {
                            if (!cleanStationConfig.serviceAdapter.deleteEntity) {
                                throw new Error('deleteEntity method not available');
                            }
                            await cleanStationConfig.serviceAdapter.deleteEntity(stationId);
                            setEditing(false);
                            onClose();
                            // Trigger grid reload
                            if (onUpdate) {
                                onUpdate();
                            }
                        } catch (error) {
                            console.error('[WSE DetailModal] Error deleting station:', error);
                            throw error;
                        }
                    } : undefined}
                    title="Edit Weather Station"
                />
            </AppModal>
        ) : (
            <AppModal
                isOpen={isOpen}
                onClose={onClose}
                width={720}
                maxHeight="90vh"
                header={
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--em3-spacing-sm, 8px)' }}>
                        <FontAwesomeIcon icon={faTowerBroadcast} />
                        {(station.name ? `${station.name} Station` : 'Weather Station') + ' Details'}
                    </span>
                }
            >
                <div className="weather-station-modal">{/* legacy hook for any external CSS targeting this class */}

                    {/* Toolbar */}
                    <div className="modal-toolbar" style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '8px',
                        padding: '12px 20px',
                        borderBottom: '1px solid var(--em3-color-border)',
                        backgroundColor: 'var(--em3-color-background-header)'
                    }}>
                        <button
                          className="btn btn-sm btn-outline"
                          style={{ fontSize: '14px' }}
                          onClick={() => {
                            if (station?.geometry?.coordinates && window.map) {
                              const [lng, lat] = station.geometry.coordinates;
                              onClose();
                              onMinimizeExplorer?.();
                              window.map.flyTo({ center: [lng, lat], zoom: 12 });
                              window.map.once('moveend', () => {
                                // Remove any existing station popup
                                document.querySelectorAll('.station-detail-popup').forEach(el => el.closest('.mapboxgl-popup')?.remove());
                                const stationPopup = new mapboxgl.Popup({ closeButton: true, closeOnClick: true, className: 'station-detail-popup', maxWidth: '260px', anchor: 'bottom', offset: 30 })
                                  .setLngLat([lng, lat])
                                  .setHTML(`
                                    <div style="font-size:13px;line-height:1.5">
                                      <div style="font-weight:700;font-size:14px;margin-bottom:4px">${station.name || 'Station'}</div>
                                      <div><b>NESDIS:</b> ${station.nesdis || '—'}</div>
                                      <div><b>Region:</b> ${station.region || '—'}</div>
                                      <div><b>Status:</b> ${station.status || '—'}</div>
                                      <div><b>CFFDRS:</b> ${station.cffdrsMode || 'off'}</div>
                                      <div><b>Elevation:</b> ${station.elevationMeters ? station.elevationMeters + 'm' : '—'}</div>
                                      <div style="margin-top:4px;font-size:11px;color:var(--em3-color-text-secondary)">${lat.toFixed(5)}, ${lng.toFixed(5)}</div>
                                      <button type="button" class="station-detail-popup-details-btn" style="margin-top:8px;width:100%;padding:6px 10px;font-size:12px;font-weight:600;color:#fff;background-color:var(--em3-color-primary, #1976d2);border:none;border-radius:4px;cursor:pointer">Details</button>
                                    </div>
                                  `)
                                  .addTo(window.map!);
                                // Bridge popup → WSE detail modal. Re-uses the
                                // `wse:open-detail` listener registered in
                                // WeatherStationExplorer to reopen the view.
                                const popupEl = stationPopup.getElement();
                                const detailsBtn = popupEl?.querySelector('.station-detail-popup-details-btn') as HTMLButtonElement | null;
                                if (detailsBtn && station?.nesdis) {
                                  detailsBtn.onclick = (e) => {
                                    e.stopPropagation();
                                    window.dispatchEvent(new CustomEvent('wse:open-detail', {
                                      detail: { nesdis: station.nesdis, name: station.name }
                                    }));
                                    stationPopup.remove();
                                  };
                                }
                              });
                            }
                          }}
                        >
                          Map
                        </button>
                        <button
                          className="btn btn-sm btn-outline"
                          style={{ fontSize: '14px' }}
                          onClick={() => {
                            openTool('weatherData', { stationId: station?.nesdis });
                            onClose();
                          }}
                        >
                          Data
                        </button>
                        {canDo('stations', 'manage_cffdrs') && (
                          <button
                            className="btn btn-sm btn-outline"
                            style={{ fontSize: '14px' }}
                            onClick={() => setShowCFFDRSModal(true)}
                          >
                            CFFDRS
                          </button>
                        )}
                        {canEditStation && <button className="btn btn-sm btn-outline" style={{ fontSize: '14px' }} onClick={() => setEditing(true)}>Edit</button>}
                        {/* Stats button removed pending design decision on what stats to surface — see #464. */}
                        {canDo('stations', 'manage_cffdrs') && (
                          <button
                            className="btn btn-sm btn-outline"
                            style={{ fontSize: '14px' }}
                            onClick={() => setShowAdminModal(true)}
                          >
                            Admin
                          </button>
                        )}
                    </div>

                    {/* Station Details Section */}
                    <div className="station-basic-info" style={{ padding: '20px', borderBottom: '1px solid var(--em3-color-border)', color: 'var(--em3-color-text-primary)' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '16px', fontSize: '14px' }}>
                            <div><strong>NESDIS:</strong> {station.nesdis || 'N/A'}</div>
                            <div><strong>Region:</strong> {station.region || 'N/A'}</div>
                            <div><strong>CFFDRS:</strong> {station.cffdrsMode || 'off'}</div>
                            <div><strong>Status:</strong> {station.status || 'N/A'}</div>
                        </div>

                        {/* Trend Graphs */}
                        {stationStatus && stationStatus.trends && (
                            <div style={{ marginTop: '16px' }}>
                                {/* Reading direction for every graph below (#767). The
                                    series arrive newest-first from the backend and are
                                    reordered for display; stating the direction removes
                                    any doubt about which end is now. */}
                                <div style={{ fontSize: '11px', fontStyle: 'italic', color: 'var(--em3-color-text-secondary)', marginBottom: '10px' }}>
                                    Chart time is left to right — 24hrs ago → Now
                                </div>

                                {/* Temperature Trend */}
                                {stationStatus.trends.temperature && stationStatus.trends.temperature.length > 0 && (
                                    <div style={{ marginBottom: '20px' }}>
                                        <div style={{ fontSize: '12px', fontWeight: 'bold', color: 'var(--em3-color-text-primary)', marginBottom: '8px' }}>
                                            Temperature (24hr) - Latest: {getLatestReadout(stationStatus).temperature}°C
                                        </div>
                                        <div style={{ height: '120px', backgroundColor: 'var(--em3-color-background-panel)', borderRadius: '8px', padding: '8px' }}>
                                            <ResponsiveContainer width="100%" height="100%">
                                                <LineChart data={formatTrendData(toChronological(stationStatus.trends.temperature), 'temperature')}>
                                                    <XAxis
                                                        dataKey="time"
                                                        tick={{ fontSize: 10 }}
                                                        interval="preserveStartEnd"
                                                    />
                                                    <YAxis
                                                        tick={{ fontSize: 10 }}
                                                        domain={['dataMin - 2', 'dataMax + 2']}
                                                    />
                                                    <Tooltip content={<CustomTooltip />} />
                                                    <ReferenceLine
                                                        y={0}
                                                        stroke="var(--em3-color-text-secondary)"
                                                        strokeDasharray="3 3"
                                                        strokeOpacity={0.5}
                                                        label="0°C"
                                                    />
                                                    <Line
                                                        type="monotone"
                                                        dataKey="value"
                                                        stroke="var(--em3-color-primary)"
                                                        strokeWidth={2}
                                                        dot={<CustomDot />}
                                                        activeDot={<CustomActiveDot />}
                                                    />
                                                </LineChart>
                                            </ResponsiveContainer>
                                        </div>
                                    </div>
                                )}

                                {/* Humidity Trend */}
                                {stationStatus.trends.humidity && stationStatus.trends.humidity.length > 0 && (
                                    <div style={{ marginBottom: '20px' }}>
                                        <div style={{ fontSize: '12px', fontWeight: 'bold', color: 'var(--em3-color-text-primary)', marginBottom: '8px' }}>
                                            Humidity (24hr) - Latest: {getLatestReadout(stationStatus).humidity}%
                                        </div>
                                        <div style={{ height: '120px', backgroundColor: 'var(--em3-color-background-panel)', borderRadius: '8px', padding: '8px' }}>
                                            <ResponsiveContainer width="100%" height="100%">
                                                <LineChart data={formatTrendData(toChronological(stationStatus.trends.humidity), 'humidity')}>
                                                    <XAxis
                                                        dataKey="time"
                                                        tick={{ fontSize: 10 }}
                                                        interval="preserveStartEnd"
                                                    />
                                                    <YAxis
                                                        tick={{ fontSize: 10 }}
                                                        domain={[0, 100]}
                                                    />
                                                    <Tooltip content={<CustomTooltip />} />
                                                    <ReferenceLine
                                                        y={25}
                                                        stroke="var(--em3-color-text-secondary)"
                                                        strokeDasharray="3 3"
                                                        strokeOpacity={0.5}
                                                        label="25%"
                                                    />
                                                    <ReferenceLine
                                                        y={75}
                                                        stroke="var(--em3-color-text-secondary)"
                                                        strokeDasharray="3 3"
                                                        strokeOpacity={0.5}
                                                        label="75%"
                                                    />
                                                    <Line
                                                        type="monotone"
                                                        dataKey="value"
                                                        stroke="var(--em3-color-primary)"
                                                        strokeWidth={2}
                                                        dot={<CustomDot />}
                                                        activeDot={<CustomActiveDot />}
                                                    />
                                                </LineChart>
                                            </ResponsiveContainer>
                                        </div>
                                    </div>
                                )}

                                {/* Rain Trend (#752) — hourly precipitation as bars.
                                    Bars rather than a line: precipitation is a
                                    discrete accumulation per hour, not a continuous
                                    quantity to interpolate between readings.
                                    The header shows a 24h TOTAL, not the latest
                                    reading: a single hour is nearly always 0 and
                                    says almost nothing on its own. */}
                                {stationStatus.trends.precipitation && stationStatus.trends.precipitation.length > 0 && (
                                    <div style={{ marginBottom: '20px' }}>
                                        <div style={{ fontSize: '12px', fontWeight: 'bold', color: 'var(--em3-color-text-primary)', marginBottom: '8px' }}>
                                            Rain (24hr) - Total: {getRain24hTotal(stationStatus) ?? 'N/A'} mm
                                        </div>
                                        <div style={{ height: '120px', backgroundColor: 'var(--em3-color-background-panel)', borderRadius: '8px', padding: '8px' }}>
                                            <ResponsiveContainer width="100%" height="100%">
                                                <BarChart data={formatTrendData(toChronological(stationStatus.trends.precipitation), 'precipitation')}>
                                                    <XAxis
                                                        dataKey="time"
                                                        tick={{ fontSize: 10 }}
                                                        interval="preserveStartEnd"
                                                    />
                                                    <YAxis
                                                        tick={{ fontSize: 10 }}
                                                        allowDecimals={true}
                                                        /* Floor the scale at 5mm so a dry window reads as
                                                           dry. Without it the axis autoscales and a 0.25mm
                                                           trace renders as a full-height bar. */
                                                        domain={[0, (dataMax: number) => Math.max(5, Math.ceil(dataMax))]}
                                                    />
                                                    <Tooltip content={<CustomTooltip />} />
                                                    <Bar
                                                        dataKey="value"
                                                        fill="#0d47a1"
                                                        isAnimationActive={false}
                                                    />
                                                </BarChart>
                                            </ResponsiveContainer>
                                        </div>
                                    </div>
                                )}

                                {/* Wind Speed Trend (with gust overlay — #676) */}
                                {stationStatus.trends.windSpeed && stationStatus.trends.windSpeed.length > 0 && (() => {
                                    // Reorder BEFORE aligning: alignGustToWind matches by timestamp and returns
                                    // one value per wind point in wind order, so the gust column follows.
                                    const windChronological = toChronological(stationStatus.trends.windSpeed);
                                    const gustCol = alignGustToWind(windChronological, stationStatus.trends.gust);
                                    const windChartData = formatTrendData(windChronological, 'windSpeed')
                                        .map((point, i) => ({ ...point, gust: gustCol[i] }));
                                    const hasGust = gustCol.some(g => g != null);
                                    return (
                                    <div style={{ marginBottom: '20px' }}>
                                        <div style={{ fontSize: '12px', fontWeight: 'bold', color: 'var(--em3-color-text-primary)', marginBottom: '8px' }}>
                                            Wind Speed (24hr) - Latest: {getLatestReadout(stationStatus).windSpeed} km/h
                                        </div>
                                        <div style={{ height: '120px', backgroundColor: 'var(--em3-color-background-panel)', borderRadius: '8px', padding: '8px' }}>
                                            <ResponsiveContainer width="100%" height="100%">
                                                <LineChart data={windChartData}>
                                                    <XAxis
                                                        dataKey="time"
                                                        tick={{ fontSize: 10 }}
                                                        interval="preserveStartEnd"
                                                    />
                                                    <YAxis
                                                        tick={{ fontSize: 10 }}
                                                        domain={[0, 'dataMax + 5']}
                                                    />
                                                    <Tooltip content={<CustomTooltip />} />
                                                    <Legend wrapperStyle={{ fontSize: 10 }} />
                                                    <ReferenceLine
                                                        y={15}
                                                        stroke="var(--em3-color-text-secondary)"
                                                        strokeDasharray="3 3"
                                                        strokeOpacity={0.5}
                                                        label="15 km/h"
                                                    />
                                                    <ReferenceLine
                                                        y={30}
                                                        stroke="var(--em3-color-text-secondary)"
                                                        strokeDasharray="3 3"
                                                        strokeOpacity={0.5}
                                                        label="30 km/h"
                                                    />
                                                    <Line
                                                        type="monotone"
                                                        dataKey="value"
                                                        name="Sustained"
                                                        stroke="var(--em3-color-primary)"
                                                        strokeWidth={2}
                                                        dot={<CustomDot />}
                                                        activeDot={<CustomActiveDot />}
                                                    />
                                                    {hasGust && (
                                                        <Line
                                                            type="monotone"
                                                            dataKey="gust"
                                                            name="Gust"
                                                            stroke="#ff9800"
                                                            strokeWidth={2}
                                                            strokeDasharray="5 3"
                                                            dot={false}
                                                            connectNulls
                                                        />
                                                    )}
                                                </LineChart>
                                            </ResponsiveContainer>
                                        </div>
                                    </div>
                                    );
                                })()}
                            </div>
                        )}

                        {station.geometry?.coordinates && (
                            <div style={{ marginTop: '12px', fontSize: '13px', color: 'var(--em3-color-text-secondary)' }}>
                                <strong>Coordinates:</strong> {station.geometry.coordinates[1]?.toFixed(6)}, {station.geometry.coordinates[0]?.toFixed(6)}
                                &nbsp;|&nbsp; {convertToDM(station.geometry.coordinates[1], 'lat')}, {convertToDM(station.geometry.coordinates[0], 'lon')}
                            </div>
                        )}
                    </div>

                    {/* Beautiful Windroses Section */}
                    <div className="windroses-section" style={{ padding: '20px' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                            {/* Spread Event Windrose */}
                            <div className="windrose-container">
                                <h3 style={{
                                    margin: '0 0 15px 0',
                                    fontSize: '16px',
                                    fontWeight: '600',
                                    color: 'var(--em3-color-danger)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}>
                                    <FontAwesomeIcon icon={faFire} />
                                    Spread Event Conditions
                                </h3>
                                <SpreadEventWindrose
                                    stationId={station.nesdis}
                                    width={300}
                                    height={300}
                                />
                            </div>

                            {/* Hourly Windrose */}
                            <div className="windrose-container">
                                <h3 style={{
                                    margin: '0 0 15px 0',
                                    fontSize: '16px',
                                    fontWeight: '600',
                                    color: 'var(--em3-color-info)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}>
                                    <FontAwesomeIcon icon={faWind} />
                                    Hourly Wind Patterns
                                </h3>
                                <HourlyWindrose
                                    stationId={station.nesdis}
                                    width={300}
                                    height={300}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Expandable Detailed Data */}
                    <details style={{ margin: '0 20px 20px 20px' }}>
                        <summary style={{ cursor: 'pointer', fontWeight: 'bold', padding: '8px', backgroundColor: 'var(--em3-color-background-header)', borderRadius: '4px', color: 'var(--em3-color-text-primary)' }}>
                            Detailed Station Information
                        </summary>
                        <div className="station-details" style={{ padding: '16px', fontSize: '13px', backgroundColor: 'var(--em3-color-background-panel)', marginTop: '8px', borderRadius: '4px', color: 'var(--em3-color-text-primary)' }}>
                            {/* Observation Timestamp */}
                            {stationStatus && (
                                <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: 'var(--em3-color-info-bg)', borderRadius: '4px', borderLeft: '3px solid var(--em3-color-info)' }}>
                                    <div style={{ fontSize: '12px', fontWeight: 'bold', color: 'var(--em3-color-info)', marginBottom: '6px' }}>
                                        OBSERVATION TIMESTAMP
                                    </div>
                                    <div style={{ fontSize: '11px', color: 'var(--em3-color-text-primary)' }}>
                                        <div><strong>UTC:</strong> {new Date(stationStatus.latestReading.timestamp).toISOString()}</div>
                                        <div><strong>Local:</strong> {new Date(stationStatus.latestReading.timestamp).toLocaleString('en-US', {
                                            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                                            year: 'numeric',
                                            month: '2-digit',
                                            day: '2-digit',
                                            hour: '2-digit',
                                            minute: '2-digit',
                                            second: '2-digit',
                                            timeZoneName: 'short'
                                        })}</div>
                                    </div>
                                </div>
                            )}

                            <div className="detail-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                <div><strong>Elevation:</strong> {station.elevationMeters ? `${station.elevationMeters}m` : 'N/A'}</div>
                                <div><strong>CFFDRS Mode:</strong> {station.cffdrsMode || 'N/A'}</div>
                                <div><strong>Contact:</strong> {station.contact || 'N/A'}</div>
                                <div><strong>Fuel Type:</strong> {station.fuelType || 'N/A'}</div>
                                <div><strong>Transmit Time:</strong> {station.xmitTime || 'N/A'}</div>
                                <div><strong>Sensor Suite:</strong> {station.sensorSuite || 'N/A'}</div>
                            </div>
                            {station.sensorTokens && (
                                <div style={{ marginTop: '12px' }}>
                                    <strong>Sensor Tokens:</strong> {Array.isArray(station.sensorTokens) ? station.sensorTokens.join(', ') : station.sensorTokens}
                                </div>
                            )}

                            {/* System Metrics Section */}
                            <div style={{ marginTop: '16px', padding: '12px', backgroundColor: 'var(--em3-color-background-header)', borderRadius: '4px', borderLeft: '3px solid var(--em3-color-info)' }}>
                                <div style={{ fontSize: '12px', fontWeight: 'bold', color: 'var(--em3-color-text-secondary)', marginBottom: '8px' }}>
                                    SYSTEM METRICS (Latest Reading)
                                </div>

                                {loadingStatus ? (
                                    <div style={{ fontSize: '11px', color: 'var(--em3-color-text-secondary)', fontStyle: 'italic', marginBottom: '8px' }}>
                                        Loading system metrics...
                                    </div>
                                ) : statusError ? (
                                    <div style={{ fontSize: '11px', color: 'var(--em3-color-danger)', fontStyle: 'italic', marginBottom: '8px' }}>
                                        Error: {statusError}
                                    </div>
                                ) : stationStatus ? (
                                    <>
                                        <div style={{ fontSize: '11px', color: 'var(--em3-color-success)', marginBottom: '8px' }}>
                                            Last updated: {stationStatus.latestReading.dataAge} • Status: {stationStatus.status.dataAvailability}
                                        </div>
                                        {/* Power and Transmission Metrics */}

                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '12px' }}>
                                            <div><strong>Battery Voltage:</strong> {stationStatus.latestReading.systemMetrics.power.vbat !== null && stationStatus.latestReading.systemMetrics.power.vbat !== undefined ? formatWithUnit(stationStatus.latestReading.systemMetrics.power.vbat, 'vbat') : 'N/A'}</div>
                                            <div><strong>Solar Voltage:</strong> {stationStatus.latestReading.systemMetrics.power.vslr !== null && stationStatus.latestReading.systemMetrics.power.vslr !== undefined ? formatWithUnit(stationStatus.latestReading.systemMetrics.power.vslr, 'vslr') : 'N/A'}</div>
                                            <div><strong>Transmitter Voltage:</strong> {stationStatus.latestReading.systemMetrics.power.vtx !== null && stationStatus.latestReading.systemMetrics.power.vtx !== undefined ? formatWithUnit(stationStatus.latestReading.systemMetrics.power.vtx, 'vtx') : 'N/A'}</div>
                                            <div><strong>Battery Current:</strong> {stationStatus.latestReading.systemMetrics.power.ibat !== null && stationStatus.latestReading.systemMetrics.power.ibat !== undefined ? formatWithUnit(stationStatus.latestReading.systemMetrics.power.ibat, 'ibat') : 'N/A'}</div>
                                            <div><strong>Solar Current:</strong> {stationStatus.latestReading.systemMetrics.power.islr !== null && stationStatus.latestReading.systemMetrics.power.islr !== undefined ? formatWithUnit(stationStatus.latestReading.systemMetrics.power.islr, 'islr') : 'N/A'}</div>
                                            <div><strong>Signal Strength:</strong> {stationStatus.latestReading.systemMetrics.transmission.signalStrength ? `${stationStatus.latestReading.systemMetrics.transmission.signalStrength}dBm` : 'N/A'}</div>
                                        </div>

                                        {/* Additional metrics */}
                                        <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid var(--em3-color-border)' }}>
                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '11px' }}>
                                                <div><strong>SWR:</strong> {stationStatus.latestReading.systemMetrics.transmission.swr !== null && stationStatus.latestReading.systemMetrics.transmission.swr !== undefined ? formatWithUnit(stationStatus.latestReading.systemMetrics.transmission.swr, 'swr') : 'N/A'}</div>
                                                <div><strong>Failure Code:</strong> {stationStatus.latestReading.systemMetrics.transmission.failureCode || 'N/A'}</div>
                                                <div><strong>Parsed:</strong> {stationStatus.latestReading.systemMetrics.timing.parsed ? 'Yes' : 'No'}</div>
                                                <div><strong>Telemetry:</strong> {stationStatus.latestReading.systemMetrics.transmission.telem !== null && stationStatus.latestReading.systemMetrics.transmission.telem !== undefined ? formatWithUnit(stationStatus.latestReading.systemMetrics.transmission.telem, 'telem') : 'N/A'}</div>
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <div style={{ fontSize: '11px', color: 'var(--em3-color-text-secondary)', fontStyle: 'italic', marginBottom: '8px' }}>
                                        No system metrics available
                                    </div>
                                )}
                            </div>
                        </div>
                    </details>                    {/* Toolbar */}
                    <div className="modal-toolbar" style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '8px',
                        padding: '12px 20px',
                        borderBottom: '1px solid var(--em3-color-border)',
                        backgroundColor: 'var(--em3-color-background-header)'
                    }}>
                        <button
                          className="btn btn-sm btn-outline"
                          style={{ fontSize: '14px' }}
                          onClick={() => {
                            if (station?.geometry?.coordinates && window.map) {
                              const [lng, lat] = station.geometry.coordinates;
                              onClose();
                              onMinimizeExplorer?.();
                              window.map.flyTo({ center: [lng, lat], zoom: 12 });
                              window.map.once('moveend', () => {
                                // Remove any existing station popup
                                document.querySelectorAll('.station-detail-popup').forEach(el => el.closest('.mapboxgl-popup')?.remove());
                                const stationPopup = new mapboxgl.Popup({ closeButton: true, closeOnClick: true, className: 'station-detail-popup', maxWidth: '260px', anchor: 'bottom', offset: 30 })
                                  .setLngLat([lng, lat])
                                  .setHTML(`
                                    <div style="font-size:13px;line-height:1.5">
                                      <div style="font-weight:700;font-size:14px;margin-bottom:4px">${station.name || 'Station'}</div>
                                      <div><b>NESDIS:</b> ${station.nesdis || '—'}</div>
                                      <div><b>Region:</b> ${station.region || '—'}</div>
                                      <div><b>Status:</b> ${station.status || '—'}</div>
                                      <div><b>CFFDRS:</b> ${station.cffdrsMode || 'off'}</div>
                                      <div><b>Elevation:</b> ${station.elevationMeters ? station.elevationMeters + 'm' : '—'}</div>
                                      <div style="margin-top:4px;font-size:11px;color:var(--em3-color-text-secondary)">${lat.toFixed(5)}, ${lng.toFixed(5)}</div>
                                      <button type="button" class="station-detail-popup-details-btn" style="margin-top:8px;width:100%;padding:6px 10px;font-size:12px;font-weight:600;color:#fff;background-color:var(--em3-color-primary, #1976d2);border:none;border-radius:4px;cursor:pointer">Details</button>
                                    </div>
                                  `)
                                  .addTo(window.map!);
                                // Bridge popup → WSE detail modal. Re-uses the
                                // `wse:open-detail` listener registered in
                                // WeatherStationExplorer to reopen the view.
                                const popupEl = stationPopup.getElement();
                                const detailsBtn = popupEl?.querySelector('.station-detail-popup-details-btn') as HTMLButtonElement | null;
                                if (detailsBtn && station?.nesdis) {
                                  detailsBtn.onclick = (e) => {
                                    e.stopPropagation();
                                    window.dispatchEvent(new CustomEvent('wse:open-detail', {
                                      detail: { nesdis: station.nesdis, name: station.name }
                                    }));
                                    stationPopup.remove();
                                  };
                                }
                              });
                            }
                          }}
                        >
                          Map
                        </button>
                        <button
                          className="btn btn-sm btn-outline"
                          style={{ fontSize: '14px' }}
                          onClick={() => {
                            openTool('weatherData', { stationId: station?.nesdis });
                            onClose();
                          }}
                        >
                          Data
                        </button>
                        {canDo('stations', 'manage_cffdrs') && (
                          <button
                            className="btn btn-sm btn-outline"
                            style={{ fontSize: '14px' }}
                            onClick={() => setShowCFFDRSModal(true)}
                          >
                            CFFDRS
                          </button>
                        )}
                        {canEditStation && <button className="btn btn-sm btn-outline" style={{ fontSize: '14px' }} onClick={() => setEditing(true)}>Edit</button>}
                        {/* Stats button removed pending design decision on what stats to surface — see #464. */}
                        {canDo('stations', 'manage_cffdrs') && (
                          <button
                            className="btn btn-sm btn-outline"
                            style={{ fontSize: '14px' }}
                            onClick={() => setShowAdminModal(true)}
                          >
                            Admin
                          </button>
                        )}
                    </div>

                    {/* ...existing code for details and windrose... */}
                </div>
            </AppModal>
        )}
        <CFFDRSWizard
          station={station}
          isOpen={showCFFDRSModal}
          onClose={() => setShowCFFDRSModal(false)}
          onComplete={() => {
            setShowCFFDRSModal(false);
            onClose();
          }}
        />
        <CFFDRSAdminModal
          station={station}
          isOpen={showAdminModal}
          onClose={() => setShowAdminModal(false)}
        />
        </>
    );
};

// Weather Station Explorer Component
const WeatherStationExplorer: React.FC<WeatherStationExplorerProps> = (props) => {
    // Provides a clean configuration-driven interface using ConfigurableBaseExplorer
    // with weather station specific modal details including windroses.
    const { canDo } = usePermissions();
    const canEditStation = canDo('stations', 'update');
    const {
        className,
        style,
        onClose: explorerOnClose,
        onSelectionChange,
        onDataChange,
        height = window.innerWidth <= 768 ? window.innerHeight - 20 : 600,
        width = window.innerWidth <= 768 ? window.innerWidth - 20 : 800,
        isWindowed = true,
        windowTitle = "Weather Station Explorer",
        initialPosition = window.innerWidth <= 768 ? { x: 10, y: 10 } : { x: 80, y: 200 },
        initialSize = window.innerWidth <= 768
            ? { width: window.innerWidth - 20, height: window.innerHeight - 20 }
            : { width: 1300, height: 800 },
        draggable = true,
        resizable = true,
        showCloseButton = true,
        showMinimizeButton = true
    } = props;

    const { isLoggedIn } = useFusionAuth();
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [selectedStation, setSelectedStation] = useState<WeatherStationEntityEntry | null>(null);

    // Imperative minimize ref for "Map" button in detail modal
    const minimizeRef = useRef<(() => void) | null>(null);

    const [refreshKey, setRefreshKey] = useState(0);
    const [dynamicZIndex, setDynamicZIndex] = useState<number | undefined>(undefined);

    const config = useMemo(() => {
        const baseConfig = { ...cleanStationConfig };
        baseConfig.features = {
            ...baseConfig.features,
            editing: {
                enabled: true,
                allowUpdate: canEditStation,
                allowDelete: false,
                allowCreate: false,
                mode: 'modal'
            }
        };
        if (!baseConfig.presentation) baseConfig.presentation = { columnConfig: [] } as any;
        if (!(baseConfig.presentation!).columnConfig) (baseConfig.presentation!).columnConfig = [];
        return baseConfig;
    }, [canEditStation]);

    const handleZIndexUpdate = React.useCallback((zIndex: number) => {
        setDynamicZIndex(zIndex);
    }, []);

    const [detailInitialEditing, setDetailInitialEditing] = useState(false);

    const handleEntitySelect = (entity: WeatherStationEntityEntry) => {
        setSelectedStation(entity);
        setDetailInitialEditing(false);
        setShowDetailModal(true);
        if (onSelectionChange) {
            onSelectionChange([entity]);
        }
    };

    const handleEntityEdit = (entity: WeatherStationEntityEntry) => {
        setSelectedStation(entity);
        setDetailInitialEditing(true);   // open straight into Edit form
        setShowDetailModal(true);
        if (onSelectionChange) {
            onSelectionChange([entity]);
        }
    };

    // Bridge from the map's weather-station popup → WSE detail modal.
    // The popup dispatches `wse:open-detail` with the station's `nesdis`;
    // we look the station up via the service adapter and open the modal
    // in view mode. Also picks up `__wsePendingDetail` stashed by ToolHost
    // when the popup fired the event before WSE was mounted.
    React.useEffect(() => {
        const openDetail = async (nesdis: string) => {
            try {
                const result = await cleanStationConfig.serviceAdapter.fetchData({
                    filters: [{ field: 'nesdis', operator: 'eq', value: nesdis }],
                });
                const station = result.data?.[0];
                if (!station) {
                    console.warn('[WSE] open-detail: no station found for nesdis', nesdis);
                    return;
                }
                setSelectedStation(station);
                setDetailInitialEditing(false);
                setShowDetailModal(true);
            } catch (err) {
                console.error('[WSE] open-detail: fetch failed', err);
            }
        };

        const handler = (e: Event) => {
            const detail = (e as CustomEvent<{ nesdis?: string; name?: string }>).detail;
            if (detail?.nesdis) openDetail(detail.nesdis);
        };
        window.addEventListener('wse:open-detail', handler);

        // Pick up any detail request fired before this listener registered
        // (ToolHost stashes it then opens this tool).
        const pending = (window as any).__wsePendingDetail;
        if (pending?.nesdis) {
            delete (window as any).__wsePendingDetail;
            openDetail(pending.nesdis);
        }

        return () => window.removeEventListener('wse:open-detail', handler);
    }, []);

    // Merge the original style with dynamic z-index if available
    const mergedStyle = dynamicZIndex !== undefined
        ? { ...style, zIndex: dynamicZIndex }
        : style;

    return (
        <div className={`weather-station-explorer ${className || ''}`} style={mergedStyle}>
            <ConfigurableBaseExplorer<WeatherStationEntityEntry>
                key={`wse-explorer-${refreshKey}`}
                config={config}
                isAuthenticated={isLoggedIn}
                onEntityEdit={handleEntityEdit}
                onEntityView={handleEntitySelect}
                onEntityCreate={onDataChange}
                isWindowed={isWindowed}
                windowTitle={windowTitle}
                initialPosition={initialPosition}
                initialSize={initialSize}
                resizable={resizable}
                draggable={draggable}
                showCloseButton={showCloseButton}
                showMinimizeButton={showMinimizeButton}
                onWindowClose={explorerOnClose}
                onZIndexUpdate={handleZIndexUpdate}
                minimizeRef={minimizeRef}
                className="weather-station-explorer-configured"
                customToolbar={
                  canDo('stations', 'create') ? (
                    <button
                      type="button"
                      className="btn-primary"
                      title="New Station"
                      aria-label="New Station"
                      onClick={() => window.dispatchEvent(new CustomEvent('wse:create-request'))}
                    >
                      <FontAwesomeIcon icon={faPlus} />
                      <span className="btn-label">New Station</span>
                    </button>
                  ) : null
                }
                help={<ContextHelp title='Help with Weather Stations' context='weather-station-help' />}
            />

            <WeatherStationDetailModal
                station={selectedStation}
                isOpen={showDetailModal}
                initialEditing={detailInitialEditing}
                onClose={() => { setShowDetailModal(false); setDetailInitialEditing(false); }}
                onMinimizeExplorer={() => minimizeRef.current?.()}
                onUpdate={() => {
                    onDataChange?.();
                    setRefreshKey(prev => prev + 1);
                }}
            />

            {canDo('stations', 'create') && (
                <WeatherStationCreateModal
                    config={cleanStationConfig}
                    onCreated={() => {
                        onDataChange?.();
                        setRefreshKey(prev => prev + 1);
                    }}
                />
            )}
        </div>
    );
};

export default WeatherStationExplorer;